import type {
  CreateCustomerContactInput,
  CustomerContact,
  PatchCustomerContactInput,
} from '@clockwise/clients/customer-contacts';
import { ApiError, isResolvableCountryInput } from '@clockwise/shared';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { MapPin } from 'lucide-react';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';
import { FormAccordionItem } from '@/components/custom/form-accordion-item';
import { Accordion } from '@/components/ui/accordion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { useUnsavedChangesGuard } from '@/hooks/use-unsaved-changes-guard';
import { customerContactsApi } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { CcEmailsField } from './cc-emails-field';
import { COUNTRY_FIELD_ERROR, CountrySelectField } from './country-select';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  customerId: string;
  /** When set, the sheet is in edit mode for this contact. */
  contact?: CustomerContact | null;
}

const CONTACT_TYPE_NONE = 'none';

// Bounded optional text — empty is allowed; the max mirrors the server so the
// client surfaces the error inline instead of relying on the server round-trip
// (whose zod-validator 400 envelope carries no per-field errors to map back).
const optionalText = (max: number) =>
  z.string().trim().max(max, `Must be ${max} characters or fewer`);

const FormSchema = z.object({
  firstName: z.string().trim().min(1, 'Required').max(120),
  lastName: z.string().trim().min(1, 'Required').max(120),
  preferredName: optionalText(120),
  title: optionalText(120),
  email: optionalText(320).refine(
    (v) => v === '' || z.string().email().safeParse(v).success,
    'Enter a valid email address',
  ),
  workPhone: optionalText(60),
  mobile: optionalText(60),
  address1: optionalText(320),
  address2: optionalText(320),
  locality: optionalText(120),
  region: optionalText(120),
  // Stored as an ISO-3166 alpha-2 code. The select only emits codes; the
  // refine catches legacy free-text values that don't resolve so they're
  // fixed explicitly rather than silently kept or cleared.
  country: optionalText(120).refine(isResolvableCountryInput, COUNTRY_FIELD_ERROR),
  postalCode: optionalText(32),
  primary: z.boolean(),
  contactType: z.enum([CONTACT_TYPE_NONE, 'billing', 'alternate']),
  // Validated as addresses are added (see CcEmailsField); the array itself is
  // always well-formed by the time it reaches here.
  ccEmails: z.array(z.string().email()).max(50),
});
type FormValues = z.infer<typeof FormSchema>;

const EMPTY: FormValues = {
  firstName: '',
  lastName: '',
  preferredName: '',
  title: '',
  email: '',
  workPhone: '',
  mobile: '',
  address1: '',
  address2: '',
  locality: '',
  region: '',
  country: '',
  postalCode: '',
  primary: false,
  contactType: CONTACT_TYPE_NONE,
  ccEmails: [],
};

const FIELD_NAMES = Object.keys(EMPTY) as (keyof FormValues)[];
const isFormField = (s: string): s is keyof FormValues =>
  (FIELD_NAMES as readonly string[]).includes(s);

// Empty string → omit (null). Trim so whitespace-only counts as empty.
const orNull = (s: string): string | null => {
  const t = s.trim();
  return t === '' ? null : t;
};

export function CustomerContactFormSheet({ open, onOpenChange, customerId, contact }: Props) {
  const queryClient = useQueryClient();
  const isEdit = contact != null;

  const form = useForm<FormValues>({
    resolver: zodResolver(FormSchema),
    defaultValues: EMPTY,
  });

  useEffect(() => {
    if (!open) return;
    if (contact) {
      form.reset({
        firstName: contact.firstName,
        lastName: contact.lastName,
        preferredName: contact.preferredName ?? '',
        title: contact.title ?? '',
        email: contact.email ?? '',
        workPhone: contact.workPhone ?? '',
        mobile: contact.mobile ?? '',
        address1: contact.address1 ?? '',
        address2: contact.address2 ?? '',
        locality: contact.locality ?? '',
        region: contact.region ?? '',
        country: contact.country ?? '',
        postalCode: contact.postalCode ?? '',
        primary: contact.primary,
        contactType: contact.contactType ?? CONTACT_TYPE_NONE,
        ccEmails: contact.ccEmails ?? [],
      });
    } else {
      form.reset(EMPTY);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, contact?.id, contact?.updatedAt]);

  const mutation = useMutation({
    mutationFn: async (v: FormValues) => {
      const body = {
        firstName: v.firstName.trim(),
        lastName: v.lastName.trim(),
        preferredName: orNull(v.preferredName),
        title: orNull(v.title),
        email: orNull(v.email),
        workPhone: orNull(v.workPhone),
        mobile: orNull(v.mobile),
        address1: orNull(v.address1),
        address2: orNull(v.address2),
        locality: orNull(v.locality),
        region: orNull(v.region),
        country: orNull(v.country),
        postalCode: orNull(v.postalCode),
        primary: v.primary,
        contactType: v.contactType === CONTACT_TYPE_NONE ? null : v.contactType,
        ccEmails: v.ccEmails,
      };
      if (isEdit && contact) {
        return customerContactsApi.patch(contact.id, body satisfies PatchCustomerContactInput);
      }
      return customerContactsApi.create(customerId, body satisfies CreateCustomerContactInput);
    },
    onSuccess: () => {
      toast.success(isEdit ? 'Contact updated' : 'Contact added');
      queryClient.invalidateQueries({ queryKey: queryKeys.customerContacts.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.audit.all });
      onOpenChange(false);
    },
    onError: (err) => {
      if (err instanceof ApiError && err.fields?.length) {
        for (const fe of err.fields) {
          if (isFormField(fe.path)) form.setError(fe.path, { message: fe.message });
        }
        toast.error('Please fix the highlighted fields');
        return;
      }
      toast.error(err instanceof Error ? err.message : 'Save failed');
    },
  });

  const onSubmit = form.handleSubmit((v) => mutation.mutate(v));
  const errors = form.formState.errors;

  const { requestOpenChange, discardDialog } = useUnsavedChangesGuard({
    open,
    isDirty: form.formState.isDirty,
    isSubmitting: mutation.isPending,
    onOpenChange,
  });

  return (
    <>
      <Sheet open={open} onOpenChange={requestOpenChange}>
        <SheetContent className="flex flex-col gap-0 sm:max-w-lg">
          <SheetHeader>
            <SheetTitle>{isEdit ? 'Edit contact' : 'Add contact'}</SheetTitle>
            <SheetDescription>
              {isEdit
                ? 'Update this contact’s details.'
                : 'Add a contact person for this customer.'}
            </SheetDescription>
          </SheetHeader>

          <form onSubmit={onSubmit} className="flex min-h-0 flex-1 flex-col">
            <div className="flex flex-1 flex-col gap-4 overflow-y-auto px-4 py-3">
              <div className="grid grid-cols-2 gap-3">
                <Field label="First name" htmlFor="cc-first" error={errors.firstName?.message}>
                  <Input
                    id="cc-first"
                    aria-invalid={!!errors.firstName}
                    {...form.register('firstName')}
                  />
                </Field>
                <Field label="Last name" htmlFor="cc-last" error={errors.lastName?.message}>
                  <Input
                    id="cc-last"
                    aria-invalid={!!errors.lastName}
                    {...form.register('lastName')}
                  />
                </Field>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <Field label="Preferred name" htmlFor="cc-preferred">
                  <Input id="cc-preferred" {...form.register('preferredName')} />
                </Field>
                <Field label="Title" htmlFor="cc-title">
                  <Input id="cc-title" {...form.register('title')} />
                </Field>
              </div>

              <Field label="Email" htmlFor="cc-email" error={errors.email?.message}>
                <Input
                  id="cc-email"
                  type="email"
                  aria-invalid={!!errors.email}
                  {...form.register('email')}
                />
              </Field>

              <div className="grid grid-cols-2 gap-3">
                <Field label="Work phone" htmlFor="cc-work">
                  <Input id="cc-work" {...form.register('workPhone')} />
                </Field>
                <Field label="Mobile" htmlFor="cc-mobile">
                  <Input id="cc-mobile" {...form.register('mobile')} />
                </Field>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <Field label="Contact type" htmlFor="cc-type">
                  <Select
                    value={form.watch('contactType')}
                    onValueChange={(v) =>
                      form.setValue('contactType', v as FormValues['contactType'], {
                        shouldDirty: true,
                      })
                    }
                  >
                    <SelectTrigger id="cc-type" className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={CONTACT_TYPE_NONE}>None</SelectItem>
                      <SelectItem value="billing">Billing</SelectItem>
                      <SelectItem value="alternate">Alternate</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
                <label className="flex items-end gap-2 pb-2 text-sm">
                  <input
                    type="checkbox"
                    className="size-4 rounded border-input"
                    checked={form.watch('primary')}
                    onChange={(e) =>
                      form.setValue('primary', e.target.checked, { shouldDirty: true })
                    }
                  />
                  <span>Primary contact</span>
                </label>
              </div>

              <CcEmailsField
                value={form.watch('ccEmails')}
                onChange={(next) => form.setValue('ccEmails', next, { shouldDirty: true })}
              />

              <Accordion type="multiple" defaultValue={['address']}>
                <FormAccordionItem
                  value="address"
                  icon={MapPin}
                  title="Address"
                  description="Where this contact can be reached by post."
                >
                  <Field label="Address line 1" htmlFor="cc-addr1">
                    <Input id="cc-addr1" {...form.register('address1')} />
                  </Field>
                  <Field label="Address line 2" htmlFor="cc-addr2">
                    <Input id="cc-addr2" {...form.register('address2')} />
                  </Field>
                  <div className="grid grid-cols-2 gap-3">
                    <Field label="Locality" htmlFor="cc-locality">
                      <Input id="cc-locality" {...form.register('locality')} />
                    </Field>
                    <Field label="Region" htmlFor="cc-region">
                      <Input id="cc-region" {...form.register('region')} />
                    </Field>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <Field label="Postal code" htmlFor="cc-postal">
                      <Input id="cc-postal" {...form.register('postalCode')} />
                    </Field>
                    <CountrySelectField form={form} name="country" id="cc-country" />
                  </div>
                </FormAccordionItem>
              </Accordion>
            </div>

            <SheetFooter className="flex-row justify-end border-t">
              <Button
                type="button"
                variant="outline"
                onClick={() => requestOpenChange(false)}
                disabled={mutation.isPending}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={mutation.isPending}>
                {mutation.isPending ? 'Saving…' : isEdit ? 'Save changes' : 'Add contact'}
              </Button>
            </SheetFooter>
          </form>
        </SheetContent>
      </Sheet>
      {discardDialog}
    </>
  );
}

function Field({
  label,
  htmlFor,
  error,
  children,
}: {
  label: string;
  htmlFor: string;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={htmlFor}>{label}</Label>
      {children}
      {error ? <p className="text-xs text-destructive">{error}</p> : null}
    </div>
  );
}
